<template>
  <main-section
    :isEditMode="isEditMode"
    :isDiff="manufacturer.isDiff"
    :isOutdated="isOutdated"
    :itemType="'MANUFACTURER'"
    :typename="'manufacturer'"
    :item="manufacturer"
    :onUpdate="updateManufacturer"
    :onCreate="createManufacturer"
    :onDelete="deleteManufacturer"
    @change-chosen="onChangeChosen"
    @revert="onRevert"
  >
    <fieldset slot="fieldset" :disabled="isOutdated">
      <div
        class="field required"
        :class="{ error: $v.manufacturer.name.$error }"
        v-diff-field.disable="manufacturerDiff.name"
      >
        <label for="manufacturerName">{{ $t('NAME') }}</label>
        <input
          id="manufacturerName"
          type="text"
          :placeholder="$t('NAME')"
          v-model.trim="manufacturer.name"
          @click="$v.manufacturer.name.$touch()"
        />
      </div>
    </fieldset>
  </main-section>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../scss/variables';
</style>

<script>
// @flow
import Vue from 'vue'
import { Application, Manufacturer, Toast, ErrorCodes, UnsavedChecker } from 'services'
import { MainSection } from 'ui'
import { take } from 'rxjs/operators'
import { required } from 'vuelidate/lib/validators'

const manufacturer = {
  name: ''
}

export default {
  name: 'manufacturer',
  mixins: [UnsavedChecker.mixin],
  components: { MainSection },
  data() {
    return {
      mode: '',
      manufacturer: { ...manufacturer },
      isOutdated: false,
      manufacturerDiff: {}
    }
  },

  computed: {
    isEditMode() {
      return this.mode === 'edit'
    }
  },

  validations: {
    manufacturer: {
      name: {
        required
      }
    }
  },

  methods: {
    readManufacturer(manufacturerId: string) {
      return Manufacturer.readManufacturer(manufacturerId)
        .then(manufacturer => {
          this.manufacturer = manufacturer
          this.isOutdated = false
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('READ', { thing: this.$i18n.tc('MANUFACTURER', 1) })
            })
          )
          console.error(err)
        })
    },

    createManufacturer() {
      if (!this.$v.$invalid) {
        return Manufacturer.createManufacturer(this.manufacturer)
          .then(res => {
            Toast.success(this.$i18n.t('CREATED', { thing: this.$i18n.tc('MANUFACTURER', 1) }), {
              remove: 5000
            })
            this.$router.push({
              name: 'manufacturer',
              params: { manufacturerId: res.id }
            })
          })
          .catch(err => {
            Toast.error(
              this.$i18n.t('ERR', {
                action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('MANUFACTURER', 1) })
              })
            )
            console.error(err)
          })
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_REQUIRED', {
            action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('MANUFACTURER', 1) }),
            thing: this.$i18n.t('ALL_REQUIRED_FIELDS')
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    updateManufacturer() {
      if (!this.$v.$invalid) {
        if (!this.isOutdated) {
          return Manufacturer.updateManufacturer(this.manufacturer.id, this.manufacturer)
            .then(res => {
              this.manufacturer.version = res.version
              Toast.success(this.$i18n.t('UPDATED', { thing: this.$i18n.tc('MANUFACTURER', 1) }), {
                remove: 5000
              })
            })
            .catch(err => {
              if (err.code === ErrorCodes.ITEM_OUTDATED) {
                this.isOutdated = true
                Toast.error(
                  this.$i18n.t('ERR_OUTDATED_VERSION', {
                    action: this.$i18n.t('UPDATE', {
                      thing: this.$i18n.tc('MANUFACTURER', 1)
                    }),
                    thing: this.$i18n.tc('MANUFACTURER', 1)
                  })
                )
              } else {
                Toast.error(
                  this.$i18n.t('ERR', {
                    action: this.$i18n.t('UPDATE', {
                      thing: this.$i18n.tc('MANUFACTURER', 1)
                    })
                  }),
                  { remove: 5000 }
                )
                console.error(err)
              }
              throw err
            })
        } else {
          Toast.error(
            this.$i18n.t('ERR_OUTDATED_VERSION', {
              action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('MANUFACTURER', 1) }),
              thing: this.$i18n.tc('MANUFACTURER', 1)
            })
          )
          return Promise.reject('Outdated version')
        }
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_REQUIRED', {
            action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('MANUFACTURER', 1) }),
            thing: this.$i18n.t('ALL_REQUIRED_FIELDS')
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    deleteManufacturer() {
      Application.readAppsUsingManufacturer(this.manufacturer.id)
        .then(usedApps => {
          if (usedApps.length) {
            const constraintText = this.$i18n.t('USED_CONSTRAINT_EX', {
              action: this.$i18n
                .t('DELETE', {
                  thing: this.$i18n.tc('MANUFACTURER', 1)
                })
                .toLowerCase(),
              list: usedApps.map((a, i) => ` ${a.name}${i < usedApps.length ? '' : ', '}`)
            })

            Toast.warning(this.$i18n.t('USED_CONSTRAINT', { thing: this.manufacturer.name }), {
              remove: 10000,
              text: constraintText
            })
            throw new Error(constraintText)
          } else {
            return Manufacturer.deleteManufacturer(this.manufacturer.id)
          }
        })
        .then(() => {
          Toast.success(this.$i18n.t('DELETED', { thing: this.$i18n.tc('MANUFACTURER', 1) }), {
            remove: 5000
          })
          this.$router.push({
            name: 'manufacturers'
          })
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('DELETE', { thing: this.$i18n.tc('MANUFACTURER', 1) })
            })
          )
          console.error(err)
        })
    },
    editMode() {
      this.mode = 'edit'
      this.readManufacturer(this.$route.params.manufacturerId)
    },

    createMode() {
      this.mode = 'create'
      this.manufacturer = { ...manufacturer }
    },

    onChangeChosen({ changedItem, fullDiff }) {
      this.manufacturer = changedItem
      this.manufacturerDiff = fullDiff
    },

    onRevert(manufacturer) {
      this.manufacturer = manufacturer
      this.manufacturerDiff = {}
    }
  },

  mounted() {
    if (this.$route.params.manufacturerId) {
      this.editMode()
    } else {
      this.createMode()
    }
  },

  watch: {
    '$route.params.manufacturerId': function(newManufacturerId) {
      this.$v.$reset()
      if (newManufacturerId) {
        this.editMode()
      } else {
        this.createMode()
      }
    }
  }
}
</script>
